import apiClient from './client'
import API from "@/api/API";
import { desktopCall, isDesktopBridgeAvailable } from '@/lib/desktop/bridge'
import { runtimeTarget } from '@/lib/runtime-target'

export interface Job {
  id: string
  command: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  args: any
  status: 'new' | 'running' | 'completed' | 'failed' | 'canceled'
  created_at: string
  started_at?: string
  completed_at?: string
  processing_time?: number
  error?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result?: any
}

export interface RequeueResponse {
  job_id: string
  requeued_job_id: string
  status: string
  message?: string
}

export interface JobListResult {
  items: Job[]
  total: number
}

export interface DesktopCommandExecuteResult {
  success?: boolean
  error_message?: string
  [key: string]: unknown
}

export const commandApi = {
  listJobs: async (
    limit: number = 20,
    status?: string,
    sourceId?: string,
    includeTotal: boolean = false
  ): Promise<JobListResult> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params: any = { limit }
    if (status && status !== 'all') {
      params.status_filter = status
    }
    if (sourceId && sourceId !== 'all') {
      params.source_id = sourceId
    }
    if (includeTotal) {
      params.include_total = true
    }
    const response = await apiClient.get(API.commands.jobs, {
      params
    })
    const data = response.data
    if (Array.isArray(data)) {
      return { items: data, total: data.length }
    }
    return {
      items: data?.items ?? [],
      total: data?.total ?? data?.items?.length ?? 0
    }
  },

  getJob: async (jobId: string): Promise<Job> => {
    const response = await apiClient.get(`${API.commands.jobs}/${jobId}`)
    return response.data
  },

  cancelJob: async (jobId: string): Promise<boolean> => {
    const response = await apiClient.post(`${API.commands.jobs}/${jobId}/cancel`)
    return response.data.success
  },

  requeueJob: async (jobId: string): Promise<RequeueResponse> => {
    const response = await apiClient.post(`${API.commands.jobs}/${jobId}/requeue`)
    return response.data
  },

  executeDesktopCommand: async (
    command: string,
    args: Record<string, unknown>,
    tenantCode?: string
  ): Promise<DesktopCommandExecuteResult> => {
    if (runtimeTarget !== 'desktop' || !isDesktopBridgeAvailable()) {
      throw new Error('Desktop bridge is not available')
    }
    return await desktopCall<DesktopCommandExecuteResult>('commands.execute', {
      command,
      args,
      ...(tenantCode ? { tenant_code: tenantCode } : {}),
    })
  },
}
